import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  InputBase,
  Typography,
} from '@mui/material';
import { S13Sheet, S13Visit } from './s13-types';
import {
  MAX_VISIT_SLOTS,
  padVisits,
  deriveLastVisitDate,
  isValidVisitDate,
} from './s13-utils';

interface S13SheetTableProps {
  sheet: S13Sheet;
  /** payload.sheets 내 이 시트의 index — 콜백에 그대로 전달 */
  sheetIndex: number;
  /** 방문 슬롯 편집 콜백. (시트 index, 구역 index, 슬롯 index, 변경 필드, 값) */
  onVisitChange: (
    sheetIndex: number,
    rowIndex: number,
    slotIndex: number,
    field: keyof S13Visit,
    value: string,
  ) => void;
  disabled?: boolean;
}

/**
 * 시트(N번구역) 하나의 편집 테이블.
 * 각 행 = 구역. areaNo/description은 읽기 전용, 방문 슬롯 4개만 편집 가능.
 * 마지막 방문일은 visits에서 파생해 읽기 전용 표시.
 */
/**
 * 화면 페이지당 구역 수 — 엑셀 양식의 페이지 단위(20구역)와 동일.
 * 한 번에 mount되는 입력 셀 수를 제한해 탭/페이지 전환을 가볍게 유지한다.
 */
const ROWS_PER_PAGE = 20;

const S13SheetTable: React.FC<S13SheetTableProps> = ({
  sheet,
  sheetIndex,
  onVisitChange,
  disabled = false,
}) => {
  const [page, setPage] = useState(0);
  const pageRows = sheet.rows.slice(
    page * ROWS_PER_PAGE,
    (page + 1) * ROWS_PER_PAGE,
  );
  const pageOffset = page * ROWS_PER_PAGE;

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ width: 90 }}>
              <strong>구역번호</strong>
            </TableCell>
            <TableCell sx={{ minWidth: 140 }}>
              <strong>구역 설명</strong>
            </TableCell>
            <TableCell sx={{ width: 110 }} align="center">
              <strong>마지막 방문일</strong>
            </TableCell>
            {Array.from({ length: MAX_VISIT_SLOTS }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableCell key={`vh-${i}`} align="center" sx={{ minWidth: 200 }}>
                <strong>{`방문 ${i + 1}`}</strong>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {pageRows.map((row, localIndex) => {
            // onVisitChange에는 시트 전체 기준의 절대 index를 넘긴다
            const rowIndex = pageOffset + localIndex;
            const slots = padVisits(row.visits);
            const lastVisit = deriveLastVisitDate(row.visits);
            return (
              <TableRow key={row.areaNo} hover>
                <TableCell sx={{ verticalAlign: 'top', pt: 2 }}>
                  {row.areaNo}
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', pt: 2 }}>
                  <Typography variant="body2">{row.description}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ verticalAlign: 'top', pt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {lastVisit || '-'}
                  </Typography>
                </TableCell>
                {slots.map((visit, slotIndex) => {
                  const dateError = !isValidVisitDate(visit.date);
                  return (
                    <TableCell
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${row.areaNo}-slot-${slotIndex}`}
                      sx={{ verticalAlign: 'top' }}
                    >
                      {/* 셀 수백 개가 한 번에 mount되는 화면이라 TextField 대신
                          경량 InputBase 사용 (탭 전환 렌더링 성능) */}
                      <InputBase
                        placeholder="방문자명"
                        value={visit.name}
                        disabled={disabled}
                        onChange={(e) =>
                          onVisitChange(
                            sheetIndex,
                            rowIndex,
                            slotIndex,
                            'name',
                            e.target.value,
                          )
                        }
                        sx={{
                          mb: 0.5,
                          fontSize: 14,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                        fullWidth
                      />
                      <InputBase
                        placeholder="YY-MM-DD"
                        value={visit.date}
                        disabled={disabled}
                        error={dateError}
                        title={dateError ? '형식: YY-MM-DD' : undefined}
                        onChange={(e) =>
                          onVisitChange(
                            sheetIndex,
                            rowIndex,
                            slotIndex,
                            'date',
                            e.target.value,
                          )
                        }
                        sx={{
                          fontSize: 14,
                          borderBottom: '1px solid',
                          borderColor: dateError ? 'error.main' : 'divider',
                          ...(dateError && { color: 'error.main' }),
                        }}
                        fullWidth
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
          {sheet.rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={3 + MAX_VISIT_SLOTS}
                align="center"
                sx={{ py: 4, color: 'text.secondary' }}
              >
                이 시트에 해당하는 구역이 없습니다
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {sheet.rows.length > ROWS_PER_PAGE && (
        <TablePagination
          component="div"
          count={sheet.rows.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} / 전체 ${count}구역 (엑셀 페이지 ${page + 1})`
          }
        />
      )}
    </TableContainer>
  );
};

// 편집 시 다른 시트(숨김 탭) 재렌더 방지 — sheet 참조가 바뀐 시트만 다시 그린다
export default React.memo(S13SheetTable);
